import { useState } from 'react';
import { Button, Group, Heading, Text } from '@optiaxiom/react';
import { useApiKeyStatus, useGenerateApiKey } from '@/lib/api';
import { ApiKeyActiveState } from './ApiKeyActiveState';
import { ApiKeyEmptyState } from './ApiKeyEmptyState';
import { ApiKeyError } from './ApiKeyError';
import { ApiKeyLoading } from './ApiKeyLoading';
import { GeneratedKeyModal } from './GeneratedKeyModal';
import { RegenerateWarningModal } from './RegenerateWarningModal';

export function ApiKeyPage() {
  const { data, isLoading, isError, isFetching, refetch } = useApiKeyStatus();
  const generate = useGenerateApiKey();
  // The plaintext key, held only while the reveal modal is open. Cleared on close.
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  const handleGenerate = () => {
    generate.mutate(undefined, {
      onSuccess: (result) => setGeneratedKey(result.plaintextKey),
    });
  };

  const handleModalClose = () => {
    setGeneratedKey(null);
  };

  // Regenerate: warn first (the old key is revoked immediately), then on confirm
  // run the same generate flow (replace + show-once). Reset clears any stale
  // pending/error state so the dialog opens in a clean warning state.
  const handleRegenerate = () => {
    generate.reset();
    setRegenerateOpen(true);
  };

  const handleRegenerateConfirm = () => {
    // Keep the warning dialog open (showing a loader) until the key is ready,
    // then hand off to the show-once modal — no blank gap in between.
    generate.mutate(undefined, {
      onSuccess: (result) => {
        setRegenerateOpen(false);
        setGeneratedKey(result.plaintextKey);
      },
    });
  };

  const hasKey = Boolean(data?.hasKey);

  return (
    <>
      <Group flexDirection="row" justifyContent="space-between" alignItems="end" gap="16">
        <Group flexDirection="column" gap="4" alignItems="start">
          <Heading level="1">API Key</Heading>
          {!isLoading && hasKey && <Text color="fg.secondary">You have an active API key.</Text>}
        </Group>
        {!isLoading && hasKey && (
          <Button appearance="primary" size='lg' fontSize='md' onClick={handleRegenerate}>
            Regenerate API Key
          </Button>
        )}
      </Group>

      {/* Loading → loader. A failed *status fetch* gets its own error state (with
          retry) so we never imply "no key" when we simply couldn't load it. Only
          on a successful fetch do we show empty (no key) vs active (has key). The
          empty state also covers the standalone/no-token case (query disabled, so
          not loading and not errored). */}
      {isLoading && <ApiKeyLoading />}
      {!isLoading && isError && (
        <ApiKeyError onRetry={() => void refetch()} retrying={isFetching} />
      )}
      {!isLoading && !isError && !hasKey && (
        <ApiKeyEmptyState
          onGenerate={handleGenerate}
          isGenerating={generate.isPending}
          error={generate.isError}
        />
      )}
      {!isLoading && !isError && hasKey && <ApiKeyActiveState />}
      <RegenerateWarningModal
        open={regenerateOpen}
        loading={generate.isPending}
        error={generate.isError}
        onCancel={() => setRegenerateOpen(false)}
        onConfirm={handleRegenerateConfirm}
      />
      <GeneratedKeyModal open={generatedKey !== null} apiKey={generatedKey} onClose={handleModalClose} />
    </>
  );
}
